"use client";

import type React from "react";
import { useState, useCallback, useRef, useEffect } from "react";
import {
  Box,
  Button,
  Flex,
  IconButton,
  useToast,
  Spinner,
} from "@chakra-ui/react";
import { Plus, Minus, Save, ArrowLeft } from "lucide-react";
import { FlowNode } from "./flow-editor/FlowNode";
import { FlowEdge } from "./flow-editor/FlowEdge";
import { EdgePropertiesDialog } from "./flow-editor/EdgePropertiesDialog";
import { NodePropertiesDialog } from "./flow-editor/NodePropertiesDialog";
import { agentService } from "../../voice-services/agentService";
import type {
  Node,
  Edge,
  Point,
  DragState,
  ConnectionState,
} from "./flow-editor/types";

interface PromptTreeEditorProps {
  onBack: () => void;
  agentId: string;
}

const PromptTreeEditor: React.FC<PromptTreeEditorProps> = ({
  onBack,
  agentId,
}) => {
  const toast = useToast();

  // State
  const [nodes, setNodes] = useState<Node[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const [selectedEdge, setSelectedEdge] = useState<Edge | null>(null);
  const [dragState, setDragState] = useState<DragState | null>(null);
  const [connectingFrom, setConnectingFrom] = useState<ConnectionState | null>(
    null,
  );
  const [mousePosition, setMousePosition] = useState<Point>({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState<Point>({ x: 0, y: 0 });
  const [showEdgeProperties, setShowEdgeProperties] = useState(false);
  const [showNodeProperties, setShowNodeProperties] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState<Point>({ x: 0, y: 0 });
  const [startingNodeId, setStartingNodeId] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [llmId, setLlmId] = useState<string | null>(null);
  const [hasStates, setHasStates] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Refs
  const editorRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const initialNodesRef = useRef<Node[]>([]);
  const initialEdgesRef = useRef<Edge[]>([]);
  const initialStartingNodeIdRef = useRef<string>("");

  // Default parameters structure for edges
  const defaultParameters = {
    type: "object",
    properties: {},
  };

  // Load LLM data and initialize the flow
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        // Get the agent to find the LLM ID
        const agent = await agentService.get(agentId);

        // ✅ Retell-safe narrowing: prompt tree only makes sense for retell-llm agents
        if (!agent || agent.response_engine?.type !== "retell-llm") {
          setHasStates(false);
          setNodes([]);
          setEdges([]);
          initialNodesRef.current = [];
          initialEdgesRef.current = [];
          initialStartingNodeIdRef.current = "";
          setLlmId(null);

          toast({
            title: "Prompt Tree not available",
            description:
              "This agent is not using a Retell LLM (retell-llm). Prompt Tree requires Retell LLM states.",
            status: "warning",
            duration: 4000,
          });

          return;
        }

        const engineLlmId = agent.response_engine.llm_id;
        if (!engineLlmId) {
          throw new Error("LLM ID not found for this agent");
        }

        setLlmId(engineLlmId);

        // Get the LLM data
        const llm = await agentService.getLLMData(engineLlmId);

        // Only initialize the flow with states if they exist
        if (llm.states && llm.states.length > 0) {
          setHasStates(true);

          // Set the starting node ID from the LLM data
          if (llm.starting_state) {
            setStartingNodeId(llm.starting_state);
            initialStartingNodeIdRef.current = llm.starting_state;
          } else if (llm.states.length > 0) {
            // If no starting_state is specified, use the first state
            setStartingNodeId(llm.states[0].name);
            initialStartingNodeIdRef.current = llm.states[0].name;
          }

          // Create nodes from states
          const startingStateName = llm.starting_state || llm.states[0].name;
          const startingStateIndex = llm.states.findIndex(
            (state: any) => state.name === startingStateName,
          );
          const nodeHeight = 140;
          const verticalSpacing = nodeHeight * 1.5;

          const stateNodes = llm.states.map((state: any, index: number) => {
            let position;
            if (state.name === startingStateName) {
              position = { x: 400, y: 100 };
            } else {
              let verticalIndex = index;
              if (index > startingStateIndex) {
                verticalIndex = index;
              } else {
                verticalIndex = index + 1;
              }

              if (index === startingStateIndex) {
                verticalIndex = 0;
              } else {
                verticalIndex = index >= startingStateIndex ? index : index + 1;
              }

              position = {
                x: 400,
                y: 100 + verticalIndex * verticalSpacing,
              };
            }

            return {
              id: state.name,
              position,
              type: (state.name === startingStateName ? "start" : "state") as
                | "start"
                | "state",
              data: {
                state_name: state.name || "",
                state_prompt: state.state_prompt || "",
                tools: state.tools || [],
                interruption_sensitivity:
                  state.interruption_sensitivity || false,
              },
            };
          });

          setNodes(stateNodes);
          initialNodesRef.current = JSON.parse(JSON.stringify(stateNodes));

          // Create edges from state transitions
          const stateEdges: Edge[] = [];
          llm.states.forEach((state: any) => {
            if (state.edges && state.edges.length > 0) {
              state.edges.forEach((edge: any, edgeIndex: number) => {
                const parameters = edge.parameters || defaultParameters;

                stateEdges.push({
                  id: `${state.name}-${edge.destination_state_name}-${edgeIndex}`,
                  from: state.name,
                  to: edge.destination_state_name,
                  fromPoint: "bottom",
                  toPoint: "top",
                  fromHandle: 0,
                  toHandle: 0,
                  label: edge.description,
                  data: {
                    parameters,
                  },
                });
              });
            }
          });

          setEdges(stateEdges);
          initialEdgesRef.current = JSON.parse(JSON.stringify(stateEdges));
        } else {
          setHasStates(false);
          setNodes([]);
          setEdges([]);
          initialNodesRef.current = [];
          initialEdgesRef.current = [];
          initialStartingNodeIdRef.current = "";
        }

        // Initialize with no unsaved changes
        setHasUnsavedChanges(false);
      } catch (error) {
        console.error("Error loading LLM data:", error);
        toast({
          title: "Error loading data",
          description: "Could not load the prompt tree data",
          status: "error",
          duration: 3000,
        });

        setHasStates(false);
        setNodes([]);
        setEdges([]);
        initialNodesRef.current = [];
        initialEdgesRef.current = [];
        initialStartingNodeIdRef.current = "";
        setLlmId(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [agentId, toast]);

  // Check for unsaved changes whenever nodes, edges, or startingNodeId changes
  useEffect(() => {
    if (isLoading) return;

    const nodesChanged =
      JSON.stringify(nodes) !== JSON.stringify(initialNodesRef.current);
    const edgesChanged =
      JSON.stringify(edges) !== JSON.stringify(initialEdgesRef.current);
    const startingNodeChanged =
      startingNodeId !== initialStartingNodeIdRef.current;

    setHasUnsavedChanges(nodesChanged || edgesChanged || startingNodeChanged);
  }, [nodes, edges, startingNodeId, isLoading]);

  // Track mouse position and handle panning
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });

      if (isPanning) {
        const dx = e.clientX - panStart.x;
        const dy = e.clientY - panStart.y;

        setPan((prevPan) => ({
          x: prevPan.x + dx,
          y: prevPan.y + dy,
        }));

        setPanStart({ x: e.clientX, y: e.clientY });
      }
    };

    const handleMouseUp = () => {
      if (isPanning) {
        setIsPanning(false);

        if (canvasRef.current) {
          canvasRef.current.style.cursor = "grab";
        }
      }
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isPanning, panStart]);

  // Handle mouse wheel zoom
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      if (e.deltaY < 0) {
        handleZoomIn();
      } else {
        handleZoomOut();
      }
    };

    const editorElement = editorRef.current;
    if (editorElement) {
      editorElement.addEventListener("wheel", handleWheel, { passive: false });
    }

    return () => {
      if (editorElement) {
        editorElement.removeEventListener("wheel", handleWheel);
      }
    };
  }, [scale, pan]);

  // Node creation
  const handleNewState = () => {
    const stateNumber = nodes.length + 1;
    const id = `new_state_${stateNumber}`;
    const newNode: Node = {
      id,
      position: { x: 100, y: 100 + nodes.length * 150 },
      title: `New State`,
      type: "state",
      data: {
        state_name: id,
        state_prompt: "",
        tools: [],
      },
    };

    if (nodes.length === 0) {
      newNode.type = "start";
      setStartingNodeId(id);
      setHasStates(true);
    }

    setNodes([...nodes, newNode]);
  };

  // Dragging
  const handleDragStart = useCallback(
    (e: React.MouseEvent, nodeId: string) => {
      e.stopPropagation();

      const node = nodes.find((n) => n.id === nodeId);
      if (!node) return;

      setDragState({
        isDragging: true,
        start: {
          x: e.clientX - node.position.x,
          y: e.clientY - node.position.y,
        },
        current: { x: e.clientX, y: e.clientY },
      });
      setSelectedNode(nodeId);
      setSelectedEdge(null);
      setShowEdgeProperties(false);
    },
    [nodes],
  );

  const handleDrag = useCallback(
    (e: React.MouseEvent, nodeId: string) => {
      if (!dragState?.isDragging) return;

      const newX = e.clientX - dragState.start.x;
      const newY = e.clientY - dragState.start.y;

      setNodes((prev) =>
        prev.map((node) =>
          node.id === nodeId
            ? { ...node, position: { x: newX, y: newY } }
            : node,
        ),
      );
    },
    [dragState],
  );

  const handleDragEnd = useCallback(() => {
    setDragState(null);
  }, []);

  const handleNodeClick = useCallback(
    (e: React.MouseEvent, nodeId: string) => {
      e.stopPropagation();
      if (!dragState) {
        setSelectedNode(nodeId);
        setSelectedEdge(null);
        setShowEdgeProperties(false);
        setShowNodeProperties(true);
      }
    },
    [dragState],
  );

  // Node update
  const handleNodeUpdate = useCallback(
    (updatedNode: Node, originalNodeId: string) => {
      const nodeToUpdate = nodes.find((n) => n.id === originalNodeId);

      if (!nodeToUpdate) {
        console.error(`Node with ID ${originalNodeId} not found`);
        return;
      }

      const idChanged = updatedNode.id !== originalNodeId;

      if (idChanged) {
        setEdges((prev) =>
          prev.map((edge) => ({
            ...edge,
            from: edge.from === originalNodeId ? updatedNode.id : edge.from,
            to: edge.to === originalNodeId ? updatedNode.id : edge.to,
          })),
        );

        if (startingNodeId === originalNodeId) {
          setStartingNodeId(updatedNode.id);
        }
      }

      setNodes((prev) =>
        prev.map((node) => (node.id === originalNodeId ? updatedNode : node)),
      );
    },
    [nodes, startingNodeId, setEdges, setStartingNodeId],
  );

  // Node deletion
  const handleNodeDelete = useCallback(
    (nodeId: string) => {
      setEdges((prev) =>
        prev.filter((edge) => edge.from !== nodeId && edge.to !== nodeId),
      );
      setNodes((prev) => prev.filter((node) => node.id !== nodeId));

      if (nodeId === startingNodeId && nodes.length > 1) {
        const newStartingNode = nodes.find((node) => node.id !== nodeId);
        if (newStartingNode) {
          setStartingNodeId(newStartingNode.id);
          setNodes((prev) =>
            prev.map((node) => ({
              ...node,
              type: node.id === newStartingNode.id ? "start" : "state",
            })),
          );
        }
      }

      if (nodes.length <= 1) {
        setHasStates(false);
        setStartingNodeId("");
      }

      setSelectedNode(null);
      setShowNodeProperties(false);
    },
    [nodes, startingNodeId],
  );

  // Set starting node
  const handleSetAsStarting = useCallback((nodeId: string) => {
    setStartingNodeId(nodeId);
    setNodes((prev) =>
      prev.map((node) => ({
        ...node,
        type: node.id === nodeId ? "start" : "state",
      })),
    );
  }, []);

  const handleBackgroundMouseDown = useCallback((e: React.MouseEvent) => {
    const nodeCheck = (e.target as HTMLElement).closest("[data-node-id]");
    const connectorCheck = (e.target as HTMLElement).closest(
      "[data-connector]",
    );

    if (nodeCheck || connectorCheck) return;

    setIsPanning(true);
    setPanStart({ x: e.clientX, y: e.clientY });

    if (canvasRef.current) {
      canvasRef.current.style.cursor = "grabbing";
    }

    setSelectedNode(null);
    setSelectedEdge(null);
    setShowEdgeProperties(false);
    setShowNodeProperties(false);
  }, []);

  // Connection handling
  const handleConnectorMouseDown = useCallback(
    (
      nodeId: string,
      point: "top" | "bottom",
      handleIndex: number,
      e: React.MouseEvent,
    ) => {
      e.stopPropagation();
      setConnectingFrom({ nodeId, point, handleIndex });
    },
    [],
  );

  const handleEditorMouseUp = useCallback(
    (e: React.MouseEvent) => {
      if (connectingFrom) {
        const elements = document.elementsFromPoint(e.clientX, e.clientY);
        let targetNodeId = null;
        let targetPoint = null;
        let targetHandleIndex = null;

        for (const el of elements) {
          if (el.hasAttribute("data-node-id"))
            targetNodeId = el.getAttribute("data-node-id");
          if (el.hasAttribute("data-connector"))
            targetPoint = el.getAttribute("data-connector");
          if (el.hasAttribute("data-handle-index")) {
            targetHandleIndex = Number.parseInt(
              el.getAttribute("data-handle-index") || "0",
              10,
            );
          }
          if (targetNodeId && targetPoint && targetHandleIndex !== null) break;
        }

        if (
          targetNodeId &&
          targetPoint &&
          targetNodeId !== connectingFrom.nodeId
        ) {
          const newEdge: Edge = {
            id: `${connectingFrom.nodeId}-${targetNodeId}-${Date.now()}`,
            from: connectingFrom.nodeId,
            to: targetNodeId,
            fromPoint: connectingFrom.point,
            toPoint: targetPoint as "top" | "bottom",
            fromHandle: connectingFrom.handleIndex,
            toHandle: targetHandleIndex || 0,
            label:
              "The condition that triggers the transition to the next state. e.g., 'If the user has provided the name'",
            data: {
              parameters: defaultParameters,
            },
          };
          setEdges((prev) => [...prev, newEdge]);
        }

        setConnectingFrom(null);
      }
    },
    [connectingFrom],
  );

  // Calculate connection line coordinates
  const getConnectionLineCoordinates = () => {
    if (!connectingFrom) return null;

    const sourceNode = nodes.find((n) => n.id === connectingFrom.nodeId);
    if (!sourceNode) return null;

    const startX = sourceNode.position.x * scale + 135 * scale;
    const startY =
      sourceNode.position.y * scale +
      (connectingFrom.point === "bottom" ? 70 * scale : 0);

    return {
      startX,
      startY,
      endX: mousePosition.x - pan.x,
      endY: mousePosition.y,
    };
  };

  // Edge handling
  const handleEdgeClick = useCallback((edge: Edge) => {
    setSelectedEdge(edge);
    setSelectedNode(null);
    setShowNodeProperties(false);
    setShowEdgeProperties(true);
  }, []);

  const handleEdgeUpdate = useCallback((updatedEdge: Edge) => {
    setEdges((prev) =>
      prev.map((edge) => (edge.id === updatedEdge.id ? updatedEdge : edge)),
    );
    setSelectedEdge(updatedEdge);
  }, []);

  const handleEdgeDelete = useCallback((edgeId: string) => {
    setEdges((prev) => prev.filter((edge) => edge.id !== edgeId));
    setSelectedEdge(null);
    setShowEdgeProperties(false);
  }, []);

  // Zoom controls
  const handleZoomIn = () => setScale((prev) => Math.min(prev + 0.1, 2));
  const handleZoomOut = () => setScale((prev) => Math.max(prev - 0.1, 0.5));

  // Save the prompt tree
  const savePromptTree = async () => {
    if (!llmId) {
      toast({
        title: "Error",
        description: "LLM ID not found",
        status: "error",
        duration: 3000,
      });
      return;
    }

    setIsSaving(true);

    try {
      const llm = await agentService.getRetellLLM(llmId);

      await agentService.updateRetellLLM(llmId, {
        ...llm,
        states: nodes.map((node) => {
          const nodeEdges = edges.filter((edge) => edge.from === node.id);

          const stateEdges = nodeEdges.map((edge) => ({
            destination_state_name: edge.to,
            description: edge.label || "Transition to next state",
            parameters: edge.data?.parameters || defaultParameters,
          }));

          const stateObject: any = {
            name: node.id,
            state_prompt: node.data?.state_prompt || "",
            tools: node.data?.tools || [],
          };

          if (stateEdges.length > 0) {
            stateObject.edges = stateEdges;
          }

          return stateObject;
        }),
        starting_state: startingNodeId || null,
      });

      initialNodesRef.current = JSON.parse(JSON.stringify(nodes));
      initialEdgesRef.current = JSON.parse(JSON.stringify(edges));
      initialStartingNodeIdRef.current = startingNodeId;

      setHasUnsavedChanges(false);

      toast({
        title: "Success",
        description: "Prompt tree saved successfully",
        status: "success",
        duration: 3000,
      });
    } catch (error) {
      console.error("Error saving prompt tree:", error);
      toast({
        title: "Error",
        description: "Failed to save prompt tree",
        status: "error",
        duration: 3000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const renderLoadingOverlay = () => {
    if (!isLoading) return null;

    return (
      <Flex
        position="absolute"
        top="0"
        left="0"
        right="0"
        bottom="0"
        zIndex="20"
        bg="rgba(243, 244, 246, 0.7)"
        justify="center"
        align="center"
      >
        <Spinner size="xl" color="primary.500" />
      </Flex>
    );
  };

  // Empty state when no nodes exist
  const renderEmptyState = () => (
    <Flex
      direction="column"
      align="center"
      justify="center"
      h="100%"
      p={8}
      textAlign="center"
    >
      {/* Empty canvas with no text */}
    </Flex>
  );

  // If there's a function like this:
  const handleSave = (updatedNode: Node) => {
    const updatedNodes = nodes.map((n) => {
      if (n.id === updatedNode.id) {
        return {
          ...updatedNode,
          data: {
            ...(updatedNode.data || {}),
            tools: updatedNode.data?.tools || [],
          },
        };
      }
      return n;
    });

    setNodes(updatedNodes);
  };

  return (
    <Box flex={1} position="relative" bg="gray.100" overflow="hidden">
      {/* Editor canvas */}
      {hasStates ? (
        <Box
          ref={editorRef}
          position="absolute"
          inset={0}
          onMouseUp={handleEditorMouseUp}
          onMouseDown={handleBackgroundMouseDown}
          onClick={(e) => {
            if (
              e.target === e.currentTarget ||
              e.target === canvasRef.current
            ) {
              setSelectedNode(null);
              setSelectedEdge(null);
              setShowEdgeProperties(false);
              setShowNodeProperties(false);
            }
          }}
        >
          <div
            ref={canvasRef}
            style={{
              width: "100%",
              height: "100%",
              position: "relative",
              cursor: "grab",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                transform: `translate(${pan.x}px, ${pan.y}px)`,
                transformOrigin: "0 0",
                width: "100%",
                height: "100%",
                position: "absolute",
              }}
            >
              <svg
                width="100%"
                height="100%"
                style={{ position: "absolute", pointerEvents: "none" }}
              >
                <defs>
                  <marker
                    id="arrowhead"
                    markerWidth="10"
                    markerHeight="7"
                    refX="9"
                    refY="3.5"
                    orient="auto"
                  >
                    <polygon points="0 0, 10 3.5, 0 7" fill="rgb(75, 85, 99)" />
                  </marker>
                </defs>

                {edges.map((edge) => {
                  const sourceNode = nodes.find((n) => n.id === edge.from);
                  const targetNode = nodes.find((n) => n.id === edge.to);
                  if (!sourceNode || !targetNode) return null;

                  return (
                    <FlowEdge
                      key={edge.id}
                      edge={edge}
                      sourcePosition={{
                        x: sourceNode.position.x * scale + 135 * scale,
                        y:
                          sourceNode.position.y * scale +
                          (edge.fromPoint === "bottom" ? 70 * scale : 0),
                      }}
                      targetPosition={{
                        x: targetNode.position.x * scale + 135 * scale,
                        y:
                          targetNode.position.y * scale +
                          (edge.toPoint === "bottom" ? 70 * scale : 0),
                      }}
                      onEdgeClick={handleEdgeClick}
                    />
                  );
                })}

                {connectingFrom &&
                  (() => {
                    const coords = getConnectionLineCoordinates();
                    if (!coords) return null;

                    return (
                      <path
                        d={`M ${coords.startX} ${coords.startY} L ${coords.endX} ${coords.endY}`}
                        stroke="rgb(75, 85, 99)"
                        strokeWidth={2}
                        fill="none"
                        strokeDasharray="4"
                        pointerEvents="none"
                      />
                    );
                  })()}
              </svg>

              {nodes.map((node) => (
                <div
                  key={node.id}
                  style={{
                    position: "absolute",
                    left: `${node.position.x * scale}px`,
                    top: `${node.position.y * scale}px`,
                    transform: `scale(${scale})`,
                    transformOrigin: "top left",
                  }}
                  data-node-id={node.id}
                  onClick={(e) => handleNodeClick(e, node.id)}
                >
                  <FlowNode
                    node={{
                      ...node,
                      position: { x: 0, y: 0 },
                      type: node.id === startingNodeId ? "start" : "state",
                    }}
                    selected={selectedNode === node.id}
                    onDragStart={(e) => handleDragStart(e, node.id)}
                    onDrag={(e) => handleDrag(e, node.id)}
                    onDragEnd={handleDragEnd}
                    onConnectorMouseDown={handleConnectorMouseDown}
                  />
                </div>
              ))}
            </div>
          </div>
        </Box>
      ) : (
        renderEmptyState()
      )}

      {/* Render loading overlay */}
      {renderLoadingOverlay()}

      {/* Node Properties Dialog */}
      {showNodeProperties &&
        selectedNode &&
        (() => {
          const node = nodes.find((n) => n.id === selectedNode);
          if (!node) return null;

          return (
            <NodePropertiesDialog
              node={node}
              onClose={() => setShowNodeProperties(false)}
              onUpdate={handleNodeUpdate}
              onDelete={handleNodeDelete}
              onSetAsStarting={handleSetAsStarting}
              isStartingNode={node.id === startingNodeId}
            />
          );
        })()}

      {/* Edge Properties Dialog */}
      {showEdgeProperties && selectedEdge && (
        <EdgePropertiesDialog
          edge={selectedEdge}
          onClose={() => setShowEdgeProperties(false)}
          onUpdate={handleEdgeUpdate}
          onDelete={handleEdgeDelete}
        />
      )}

      {/* Bottom controls */}
      <Flex
        position="absolute"
        bottom="20px"
        left="50%"
        transform="translateX(-50%)"
        bg="white"
        borderRadius="md"
        border="1px solid"
        borderColor="gray.200"
        overflow="hidden"
        zIndex={10}
      >
        <Button
          leftIcon={<Plus size={16} />}
          bg="transparent"
          color="gray.800"
          _hover={{ bg: "gray.100" }}
          borderRadius="0"
          borderRight="1px solid"
          borderColor="gray.200"
          px={4}
          onClick={handleNewState}
        >
          New State
        </Button>
        <IconButton
          aria-label="Zoom in"
          icon={<Plus size={16} />}
          bg="transparent"
          color="gray.800"
          _hover={{ bg: "gray.100" }}
          borderRadius="0"
          borderRight="1px solid"
          borderColor="gray.200"
          onClick={handleZoomIn}
        />
        <IconButton
          aria-label="Zoom out"
          icon={<Minus size={16} />}
          bg="transparent"
          color="gray.800"
          _hover={{ bg: "gray.100" }}
          borderRadius="0"
          onClick={handleZoomOut}
        />
      </Flex>

      {/* Save and Back buttons */}
      <Flex position="absolute" top="20px" left="20px" gap={2} zIndex={10}>
        <IconButton
          aria-label="Back"
          icon={<ArrowLeft />}
          variant="solid"
          colorScheme="gray"
          color="gray.100"
          onClick={onBack}
        />
        {hasUnsavedChanges && (
          <Button
            leftIcon={<Save size={16} />}
            colorScheme="gray"
            _hover={{ bg: "gray.400" }}
            background="gray.300"
            textColor="gray.800"
            onClick={savePromptTree}
            isLoading={isSaving}
            isDisabled={isSaving}
          >
            Save
          </Button>
        )}
      </Flex>

      {/* Zoom indicator */}
      <Box
        position="absolute"
        bottom="20px"
        right="60px"
        bg="white"
        borderRadius="md"
        border="1px solid"
        borderColor="gray.200"
        px={2}
        py={1}
        zIndex={10}
        fontSize="sm"
        color="gray.600"
      >
        {Math.round(scale * 100)}%
      </Box>
    </Box>
  );
};

export default PromptTreeEditor;
